<template>
  <div class="d-widget-content" :style="contentStyle">
    <div
      class="d-locus-card-wrapper fts-scrollbar fts-scrollbar-overlay"
      v-for="col in widget.columns" :key="col.id">
      <!-- title -->
      <div class="d-locus-card-header">
        <div class="d-locus-card-title" v-if="col.name">{{col.name}}</div>
        <div class="d-locus-card-description" v-if="col.description">{{col.description}}</div>
      </div>
      <!-- skeleton -->
      <template v-if="loading">
        <skeleton-locus class="d-locus-card-content" />
        <skeleton-locus class="d-locus-card-content" />
      </template>
      <!-- el -->
      <template v-else>
        <div class="d-locus-no-result" v-if="!viewData[col.fieldName] || viewData[col.fieldName] && viewData[col.fieldName].length === 0">
          No result.
        </div>
        <div class="d-locus-card-content"
          v-for="(x, ind) in viewData[col.fieldName]" 
          :key="`${col.fieldName}-${ind}-${x.id}`"
          @click="open(x)"
          v-else>
          <!-- alpha -->
          <div class="d-calendar-event-row-alpha">
            <div class="d-calendar-event-row-alpha-l">
              <!-- date -->
              <div class="d-calendar-event-date" v-if="calendarEventDate(x.calendarStartDate, x.calendarEndDate)">
                {{calendarEventDate(x.calendarStartDate, x.calendarEndDate)}}
              </div>       
              <!-- date field -->
              <div class="d-calendar-event-date-field" v-if="x.startField || x.endField">
                <div v-if="x.startField">{{x.startField}}</div>
                <template v-if="x.startField && x.endField">&nbsp;-&nbsp;</template>
                <div v-if="x.endField">{{x.endField}}</div>
              </div>
              <!-- title -->
              <div class="d-title">{{x.title}}</div>
            </div>
            <!-- state -->
            <fts-state :state="x.locusState" class="d-state" />
            <!-- assign -->
            <fts-assign-to 
              :formData="x"
              :max-length="3"
              view-mode
              avatar-mode 
              :avatar-size="22"
              disabled-date
              class="d-assign stopPropagation" 
              v-if="x.assignees && x.assignees.length" />
          </div>
          <!-- beta -->
          <div class="d-locus-row-beta">
            <div class="d-locus-row-beta-l">
              <!-- document -->
              <div class="d-document-id">{{x.document.documentId}}</div>
              <!-- module -->
              <fts-label-group
                :item-id="x.vectorId"
                :list="workspaceModules"
                emoji
                :emoji-size="12"
                class="d-module"
                v-if="x.vectorId"
              />
            </div>
            <!-- audit created -->
            <div class="d-audit audit-created" v-if="x.audit && x.audit.createdBy">
              <div class="d-audit-by">by</div>
              <fts-profile :userName="x.audit.createdBy" class="d-audit-profile" />
              <fts-tippy class="d-audit-date" :content="moment(x.audit.createdDate).format('llll')">
                <template #trigger>{{dateFormat(x.audit.createdDate)}}</template>
              </fts-tippy>
            </div>
          </div>
          <!-- gamma -->
          <div class="d-locus-row-gamma">
            <!-- collaboration -->
            <div class="d-locus-row-gamma-l">
              <div class="d-collaboration fts-collaboration" v-if="x.collaborationFlags">
                <div class="collaboration-wrapper" v-if="x.collaborationFlags === 2 || x.collaborationFlags === 10">
                  <div 
                    class="collaboration-trigger collaboration-intention"
                    :class="{ 'collaboration-last': x.collaborationFlags === 10}">
                    <i class="fts-icon-exclamation-triangle" />
                  </div>
                </div>
                <div class="collaboration-wrapper" v-if="x.collaborationFlags === 1 || x.collaborationFlags === 5">
                  <div 
                    class="collaboration-trigger collaboration-comment"
                    :class="{ 'collaboration-last': x.collaborationFlags === 5}">
                    <i class="fts-icon-comment" />
                  </div>
                  <div class="collaboration-count" v-if="x.commentCount">{{x.commentCount}}</div>
                </div>
              </div>
              
              <div class="d-audit audit-updated" v-if="x.audit && x.audit.updatedBy">
                <!-- audit updated -->
                <fts-profile :userName="x.audit.updatedBy" class="d-audit-profile" />
                <fts-tippy class="d-audit-date" :content="moment(x.audit.updatedDate).format('llll')">
                  <template #trigger>{{dateFormat(x.audit.updatedDate)}}</template>
                </fts-tippy>
              </div>
            </div>
            <!-- due -->
            <fts-tippy 
              :content="moment(x.due).format('ll')" 
              class="d-due"
              :class="{'overdue': overdue(x.due)}" 
              v-if="x.due"> 
              <template #trigger>{{calendarRelativeTime(x.due)}}</template>
            </fts-tippy>
          </div>
        </div>
      </template>
    </div>
    <!-- calendar event -->
    <div class="d-calendar-event fts-text-right">
      <router-link to="calendar" class="fts-link-primary">
        view my calendar
      </router-link>
    </div>
  </div>
</template>

<script>
import moment from "moment";

import commons from '../../commons/commons';
import { clientStore } from '../../store/clientStore';
import { store as canvasDesignStore } from "../../store/canvasDesignStore";

import FTSTippy from "../../elements/FTSTippy";
import FTSProfile from "../../elements/FTSProfile";
import FTSState from "../../elements/FTSState";
import FTSAssignTo from '../../elements/FTSAssignTo';
import FTSLabelGroup from "../../elements/display/FTSLabelGroup";
import SkeletonLocus from "../../elements/skeleton-screen/dashboard/SkeletonLocus";

export default {
  props: {
    widget: {
      type: Object,
      default: () => ({}),
    },
    viewData: {
      type: Object,
      default: () => ({}),
    },
    loading: Boolean,
  },
  components: {
    "fts-tippy": FTSTippy,
    "fts-profile": FTSProfile,
    "fts-state": FTSState,
    "fts-assign-to": FTSAssignTo,
    "fts-label-group": FTSLabelGroup,
    "skeleton-locus": SkeletonLocus,
  },
  methods: {
    moment,
    dateFormat(date) {
      return commons.momentRelativeTime(date)
    },
    calendarRelativeTime(date) {
      return commons.calendarRelativeTime(date)
    },
    calendarEventDate(startDate, endDate) {
      if (startDate && endDate) 
        return `${moment(startDate).format('DD/M')} - ${moment(endDate).format('DD/M')}`;
      else if (startDate) 
        return moment(startDate).calendar(null, {
          lastWeek: () => `[${moment(startDate).format('DD/M')}]`,
          lastDay: () => `[${moment(startDate).format('DD/M')}]`,
          sameDay: '[Today]',
          nextDay: '[Tomorrow]',
          nextWeek: () => `[${moment(startDate).format('DD/M')}]`,
          sameElse: () => `[${moment(startDate).format('DD/M')}]`,
        })
    },
    overdue(date) {
      return moment(date).isBefore(moment().startOf('day')) 
    },
    open(item) {
      if (commons.elementStopPropagation()) return;
      this.$emit("open", item);
    },
  },
  computed: {
    contentStyle() {
      if (this.widget.color)
        return {
          background: this.widget.color
        }
    },
    workspaceModules() {
      if (
        Array.isArray(clientStore.state.workspaceModules) && 
        clientStore.state.workspaceModules.length
      )
        return clientStore.state.workspaceModules;
      else if (
        Array.isArray(canvasDesignStore.getters.workspaceModules) && 
        canvasDesignStore.getters.workspaceModules.length
      )
        return canvasDesignStore.getters.workspaceModules;
      
      return [];
    },
  }
}
</script>

<style lang="scss" scoped>
@import "./src/assets/scss/vars.scss";

.d-widget-content {
  .d-calendar-event {
    margin: .75rem .575rem 0;
    font-size: .875rem;
  }
}
</style>